'use client';

import { useTranslations } from 'next-intl';
import { useDateFormatter } from '@terra/ui-formatters';
import { Box, HStack, VStack } from '@terra/ui-styled-system';
import type { WebPageFeatureReturnDto } from '@terra/shared-contracts-web-pages';
import { Avatar } from '../../../components/avatar';
import * as StyledAvatar from '../../../components/styled/avatar';
import {
  BodyStrong,
  LabelDefault,
  MetaDefault,
} from '../../../components/typography';

type FeatureAuditEvent = WebPageFeatureReturnDto['auditTrail'][number];

type FeatureAuditTrailSectionProps = {
  auditTrail: FeatureAuditEvent[];
};

const TerraMarkIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 14 14"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5.11422 2.11838C5.39241 1.44677 5.53559 0.726944 5.53559 0H10.3375C10.3375 1.35755 10.0702 2.7018 9.55065 3.95601C9.34898 4.44289 9.11084 4.91249 8.83879 5.36102C8.75408 5.50069 8.93427 5.69018 9.07803 5.61263C10.5728 4.80621 12.2708 4.38632 14 4.38632V9.18828C12.5319 9.18828 11.1105 9.7581 10.0724 10.7962C9.20286 11.6657 8.65248 12.7947 8.49664 14.0012H3.67341C3.78711 12.347 4.29657 10.753 5.14776 9.3495C5.23247 9.20983 5.05229 9.0203 4.90854 9.09786C4.59951 9.26459 4.28162 9.41577 3.95601 9.55065C2.7018 10.0702 1.35755 10.3375 0 10.3375L7.21695e-07 5.53559C0.726944 5.53559 1.44677 5.39241 2.11838 5.11422C2.78999 4.83603 3.40023 4.42828 3.91425 3.91425C4.42828 3.40022 4.83603 2.78999 5.11422 2.11838Z"
      fill="currentColor"
    />
  </svg>
);

const TerraSystemAvatar = ({ label }: { label: string }) => (
  <StyledAvatar.Root size="xxs" aria-label={label}>
    <StyledAvatar.Fallback color="fg.accent.default">
      <TerraMarkIcon />
    </StyledAvatar.Fallback>
  </StyledAvatar.Root>
);

const FeatureAuditActorAvatar = ({
  event,
  systemActorLabel,
}: {
  event: FeatureAuditEvent;
  systemActorLabel: string;
}) => {
  const hasHumanActor =
    !!event.isTerraUser || !!event.actorName || !!event.actorPicture;

  if (!hasHumanActor) {
    return <TerraSystemAvatar label={systemActorLabel} />;
  }

  return <Avatar size="xxs" name={event.actorName} src={event.actorPicture} />;
};

export const FeatureAuditTrailSection = ({
  auditTrail,
}: FeatureAuditTrailSectionProps) => {
  const t = useTranslations(
    'designSystem.applicationAssets.drawer.features.auditTrail',
  );
  const tFindings = useTranslations('designSystem.findings.findingDrawer');
  const dateFormatter = useDateFormatter();

  return (
    <Box>
      <BodyStrong color="fg.default" mb={2}>
        {t('title')}
      </BodyStrong>
      <VStack gap={0} alignItems="stretch" w="full">
        {auditTrail.map((event, index) => {
          const actorLabel =
            event.actorName ??
            (event.isTerraUser
              ? tFindings('audit.terraUser')
              : t('systemActor'));
          const isLastEvent = index === auditTrail.length - 1;

          return (
            <HStack
              key={`${event.kind}-${event.occurredAt.toISOString()}-${index}`}
              alignItems="stretch"
              w="full"
              gap={3}
            >
              <VStack alignItems="center" gap={0} flexShrink={0}>
                <FeatureAuditActorAvatar
                  event={event}
                  systemActorLabel={actorLabel}
                />
                {!isLastEvent && (
                  <Box width="1px" flex={1} minH={4} bg="border.default" />
                )}
              </VStack>
              <HStack
                gap={2}
                alignItems="center"
                flexWrap="wrap"
                minH={6}
                minW={0}
                pb={isLastEvent ? 0 : 4}
              >
                <MetaDefault color="fg.muted" flexShrink={0}>
                  {actorLabel}
                </MetaDefault>
                <LabelDefault color="fg.default">
                  {t(`events.${event.kind}`)}
                </LabelDefault>
                <MetaDefault color="fg.muted" flexShrink={0}>
                  {dateFormatter.fromString(event.occurredAt.toISOString())}
                </MetaDefault>
              </HStack>
            </HStack>
          );
        })}
      </VStack>
    </Box>
  );
};
